const TARGET_SELECTOR = [
  'main[data-template^="product"] .fp-house-salon__image',
  'main[data-template^="product"] .fp-pdp__image',
  'main[data-template^="product"] .fp-merch-card__image',
  'main[data-template^="collection"] .card-gallery img',
  'main[data-template^="search"] .card-gallery img',
  '.predictive-search-results__card--product .fp-fit-contain .resource-card__image',
].join(',');

const DEFAULT_CANVAS = '#ffffff';
const sampledColors = new Map();

function averageEdgeColor(image, minBrightness = 205) {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context || !image.naturalWidth || !image.naturalHeight) return null;

  canvas.width = 24;
  canvas.height = 24;
  context.drawImage(image, 0, 0, canvas.width, canvas.height);

  const points = [];
  for (let index = 0; index < canvas.width; index += 2) {
    points.push([index, 0], [index, canvas.height - 1]);
  }
  for (let index = 2; index < canvas.height - 2; index += 2) {
    points.push([0, index], [canvas.width - 1, index]);
  }

  let red = 0;
  let green = 0;
  let blue = 0;
  let count = 0;
  for (const [x, y] of points) {
    const pixel = context.getImageData(x, y, 1, 1).data;
    if (pixel[3] < 230) continue;
    red += pixel[0];
    green += pixel[1];
    blue += pixel[2];
    count += 1;
  }

  if (!count) return null;
  const channels = [red, green, blue].map((value) => Math.round(value / count));
  const spread = Math.max(...channels) - Math.min(...channels);
  const brightness = channels.reduce((sum, value) => sum + value, 0) / 3;

  // Only extend neutral, light studio fields. Colorful/editorial scenes keep house white.
  if (brightness < minBrightness || spread > 18) return null;
  return `rgb(${channels.join(' ')})`;
}

async function sampledEdgeColor(source, minBrightness = 205) {
  if (!source) return null;
  const key = `${source}|${minBrightness}`;
  if (!sampledColors.has(key)) {
    sampledColors.set(key, new Promise((resolve) => {
      const sample = new Image();
      sample.crossOrigin = 'anonymous';
      sample.onload = () => {
        try { resolve(averageEdgeColor(sample, minBrightness)); }
        catch (_error) { resolve(null); }
      };
      sample.onerror = () => resolve(null);
      sample.src = source;
    }));
  }
  return sampledColors.get(key);
}

function applyCanvas(image) {
  if (!(image instanceof HTMLImageElement) || image.dataset.fpCanvasReady === 'true') return;
  const finish = async () => {
    try {
      // V10 product photos carry a 64px copy to sample, so a large photo is never downloaded twice
      const sample = image.closest('[data-fp-sample]')?.dataset.fpSample;
      // product-page studio photos often sit on a mid-grey sweep: extend neutral greys there too, so the photo
      // fills its frame; elsewhere only light studio fields are extended
      const color = await sampledEdgeColor(sample || image.currentSrc || image.src, sample ? 120 : 205) || DEFAULT_CANVAS;
      // each photo paints its own frame: a grid card's main photo its slide, the hover photo the card (inherited by
      // the hover layer only, since the slide keeps its own value)
      const media = image.closest('.fp-pdp__figure, .fp-house-salon__media-item, .fp-merch-card__media, .card-gallery .product-media-container, .card-gallery, .resource-card__media');
      media?.style.setProperty('--fp-photo-canvas', color);
      image.dataset.fpCanvasReady = 'true';
    } catch (_error) {
      image.dataset.fpCanvasReady = 'true';
    }
  };
  if (image.complete || image.closest('[data-fp-sample]')) finish();
  else image.addEventListener('load', finish, { once: true });
}

function scan(root = document) {
  root.querySelectorAll?.(TARGET_SELECTOR).forEach(applyCanvas);
}

scan();
const observer = new MutationObserver((records) => {
  for (const record of records) {
    for (const node of record.addedNodes) {
      if (!(node instanceof Element)) continue;
      if (node.matches?.(TARGET_SELECTOR)) applyCanvas(node);
      scan(node);
    }
  }
});
observer.observe(document.documentElement, { childList: true, subtree: true });
